import React, { useState } from "react";
import {
  TextField, Button, Grid, Box, FormControl, FormLabel,
  IconButton, MenuItem, Select, Typography, Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#D5D7DA",
      borderRadius: "10px",
    },
    "&:hover fieldset": { borderColor: "#4A7BA8" },
    "&.Mui-focused fieldset": { borderColor: "#244C77" },
  },
}));

const GRADE_OPTIONS = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"
];

const PickupDropForm = ({ handleClose }) => {
  const [formValues, setFormValues] = useState({
    studentName: "",
    registrationNo: "",
    grade: "",
    parentName: "",
    parent1Email: "",
    parent2Name: "",
    parent2Email: "",
    contactNumber: "",
    contactNumber2: "",
    pickupPerson: "",
    relationship: "",
    emergencyPersonName: "",
    emergencyPersonContact: "",
    pickupDate: new Date().toISOString().split("T")[0],
    pickupTime: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    ["studentName", "grade", "parentName", "pickupPerson", "contactNumber", "pickupDate"].forEach((f) => {
      if (!formValues[f]) newErrors[f] = "This field is required.";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await postData("student/pickup-and-drop/create", formValues);
      if (response?.success || response?.data) {
        setToast({ open: true, severity: "success", message: "Pickup/drop request submitted successfully!" });
        setTimeout(() => handleClose(), 1500);
      }
    } catch (error) {
      setToast({ open: true, severity: "error", message: "Failed to submit request. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  const styles = {
    formContainer: { padding: "24px", minWidth: "480px" },
    label: { color: "#8F95B2", fontSize: "13px", mb: 0.5 },
    buttonContainer: { display: "flex", justifyContent: "center", gap: 2, mt: 3, mb: 1 },
    cancelButton: {
      color: "#414651", fontSize: "14px", textTransform: "none",
      padding: "8px 32px", borderRadius: "10px", border: "1px solid #D5D7DA",
    },
    submitButton: {
      background: "linear-gradient(90deg, #4A7BA8, #244C77)",
      color: "#FFFFFF", fontSize: "14px", textTransform: "none",
      padding: "8px 32px", borderRadius: "10px",
    },
  };

  return (
    <Box component="form" sx={styles.formContainer} onSubmit={handleSubmit}>
      <Toast open={toast.open} handleClose={handleToastClose} severity={toast.severity} message={toast.message} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>Pick-up & Drop Request</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Warning Banner */}
      <Alert
        severity="warning"
        sx={{
          mb: 2,
          borderRadius: "10px",
          backgroundColor: "#fff3e0",
          color: "#e65100",
          "& .MuiAlert-icon": { color: "#e65100" },
          fontSize: "13px",
          fontWeight: 500,
        }}
      >
        You can schedule pickup till 6:30 PM only
      </Alert>

      <Grid container spacing={2} px={1}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Student Name *</FormLabel>
            <StyledTextField
              size="small" name="studentName" value={formValues.studentName}
              onChange={handleChange} error={!!errors.studentName} helperText={errors.studentName}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Grade *</FormLabel>
            <Select
              size="small" name="grade" value={formValues.grade} onChange={handleChange}
              displayEmpty error={!!errors.grade}
              sx={{ borderRadius: "10px", "& fieldset": { borderColor: "#D5D7DA" } }}
            >
              <MenuItem value="" disabled>Select Grade</MenuItem>
              {GRADE_OPTIONS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
            {errors.grade && <Typography sx={{ fontSize: "11px", color: "#f44336", ml: 1.5 }}>{errors.grade}</Typography>}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent / Guardian Name *</FormLabel>
            <StyledTextField
              size="small" name="parentName" value={formValues.parentName}
              onChange={handleChange} error={!!errors.parentName} helperText={errors.parentName}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent 1 Email Id</FormLabel>
            <StyledTextField
              size="small" type="email" name="parent1Email" value={formValues.parent1Email}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent 2 Name / Guardian 2</FormLabel>
            <StyledTextField
              size="small" name="parent2Name" value={formValues.parent2Name}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent 2 Email Id</FormLabel>
            <StyledTextField
              size="small" type="email" name="parent2Email" value={formValues.parent2Email}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Pickup Person *</FormLabel>
            <StyledTextField
              size="small" name="pickupPerson" value={formValues.pickupPerson}
              onChange={handleChange} error={!!errors.pickupPerson} helperText={errors.pickupPerson}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Contact Number *</FormLabel>
            <StyledTextField
              size="small" name="contactNumber" value={formValues.contactNumber}
              onChange={handleChange} error={!!errors.contactNumber} helperText={errors.contactNumber}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent/Guardian Contact No 2</FormLabel>
            <StyledTextField
              size="small" name="contactNumber2" value={formValues.contactNumber2}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Relationship to Student</FormLabel>
            <StyledTextField
              size="small" name="relationship" value={formValues.relationship}
              onChange={handleChange} placeholder="e.g. Parent, Guardian, Sibling"
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Emergency Person Name</FormLabel>
            <StyledTextField
              size="small" name="emergencyPersonName" value={formValues.emergencyPersonName}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Emergency Person Contact No</FormLabel>
            <StyledTextField
              size="small" name="emergencyPersonContact" value={formValues.emergencyPersonContact}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Pickup Date *</FormLabel>
            <StyledTextField
              size="small" name="pickupDate" type="date" value={formValues.pickupDate}
              onChange={handleChange} error={!!errors.pickupDate} helperText={errors.pickupDate}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Pickup Time</FormLabel>
            <StyledTextField
              size="small" name="pickupTime" type="time" value={formValues.pickupTime}
              onChange={handleChange} InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Additional Notes</FormLabel>
            <StyledTextField
              size="small" name="notes" multiline rows={2}
              value={formValues.notes} onChange={handleChange}
              placeholder="Any special instructions..."
            />
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={styles.buttonContainer}>
        <Button onClick={handleClose} sx={styles.cancelButton} disabled={loading}>Cancel</Button>
        <Button type="submit" sx={styles.submitButton} disabled={loading}>
          {loading ? "Submitting..." : "Submit Request"}
        </Button>
      </Box>
    </Box>
  );
};

export default PickupDropForm;
